import { Link, type LinkProps } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * TTTabBar — three tabs (Главная / История / Профиль), icon 20px + 11px semibold label,
 * 1px top hairline, padding 8px top / 14px bottom, 3px between icon and label.
 *
 * Two things the mobile clients solved that the web has to solve differently:
 *
 * - **Home-indicator clearance.** iOS reserves space below the bar; here that is
 *   `env(safe-area-inset-bottom)` on the bar itself.
 * - **Tabs are real links.** The mobile clients switch a state variable; a PWA that did the same
 *   would break the browser's back button and make every tab unshareable. These are `<a>`s with
 *   real URLs, and `aria-current` — not colour — is what announces the selected one.
 */
export interface Tab {
  /** Typed against the route tree, so a tab can never point at a path that does not exist. */
  to: LinkProps['to'];
  label: string;
  icon: ReactNode;
}

export function TabBar({ tabs }: { tabs: Tab[] }) {
  return (
    <nav
      className="sticky bottom-0 shrink-0 border-t border-line bg-surface"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Основная навигация"
    >
      <ul className="mx-auto flex max-w-[560px]">
        {tabs.map((tab) => (
          <li key={String(tab.to)} className="flex-1">
            {/* The colour lives in activeProps/inactiveProps, never in the base className:
                TanStack Router APPENDS the active class rather than replacing it, so a base
                `text-ink-soft` would still be on the element next to `text-accent`, and which
                one wins would come down to the order Tailwind happened to emit them in. */}
            <Link
              to={tab.to}
              activeOptions={{ exact: true }}
              className="flex w-full flex-col items-center gap-[3px] pb-[14px] pt-2"
              activeProps={{ className: 'text-accent', 'aria-current': 'page' }}
              inactiveProps={{ className: 'text-ink-soft' }}
            >
              <span className="grid h-5 w-5 place-items-center" aria-hidden="true">
                {tab.icon}
              </span>
              {/* Межстрочие относительное, а не жёсткие 14px: при системном увеличении шрифта
                  («Масштабирование текста» в Android, «Минимальный размер шрифта» в Chrome) буквы
                  растут, а px-строка нет, — и подпись выпирала за свою строку, съедая и без того
                  трёхпиксельный просвет до иконки: значок и слово смыкались. */}
              <span className="text-[11px] font-semibold leading-[1.27]">{tab.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
